import { PageCanvas } from "@/components/page-canvas";
import { Form, Head, Link, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import AppLayout from "@/layouts/app_layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  newLocationServicePath,
  editLocationServicePath,
  newLocationServiceOfferingPath,
  editLocationServiceOfferingPath,
  locationServiceArchivalPath,
} from "@/routes";
import type { Service, Offering } from "./types";

export default function Services({
  services,
  offerings,
}: {
  services: Service[];
  offerings: Offering[];
}) {
  const { auth, errors } = usePage().props;
  const { t, i18n } = useTranslation();
  if (!auth.location) return null;
  const locationId = auth.location.id;
  const manage = auth.user?.role === "admin";
  return (
    <AppLayout breadcrumbs={[]}>
      <Head title={t("services.title")} />
      <PageCanvas>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h1 className="text-page font-display">{t("services.title")}</h1>
          {manage && (
            <Button render={<Link href={newLocationServicePath(locationId)} />}>
              {t("services.add")}
            </Button>
          )}
        </div>
        <p className="text-muted-foreground text-sm">
          {t("services.local_help", { location: auth.location.name })}
        </p>
        {Object.keys(errors || {}).length > 0 && (
          <div role="alert" className="text-destructive text-sm">
            {Object.values(errors).map((error, i) => (
              <p key={i}>{error}</p>
            ))}
          </div>
        )}
        <Card>
          <CardContent className="divide-y">
            {services.length === 0 && (
              <p className="text-muted-foreground py-6">
                {t("services.empty")}
              </p>
            )}
            {services.map((service) => {
              const offering = offerings.find(
                (item) => item.service_id === service.id,
              );
              return (
                <article key={service.id} className="space-y-3 py-5">
                  <div className="flex flex-wrap items-center gap-3">
                    <h2 className="font-semibold break-words">
                      {service.name}
                    </h2>
                    <Badge variant="secondary">
                      {t(
                        service.archived
                          ? "services.archived"
                          : offering?.available
                            ? "services.available"
                            : "services.unavailable",
                      )}
                    </Badge>
                  </div>
                  <p className="text-muted-foreground text-sm whitespace-pre-wrap break-words">
                    {service.description}
                  </p>
                  {offering && (
                    <p className="text-sm">
                      {new Intl.NumberFormat(i18n.language, {
                        style: "currency",
                        currency: offering.currency,
                      }).format(Number(offering.price))}{" "}
                      ·{" "}
                      {t("services.duration", {
                        count: offering.estimated_minutes,
                      })}{" "}
                      · {t(`services.${offering.tax_mode}`)} (
                      {new Intl.NumberFormat(i18n.language, {
                        maximumFractionDigits: 4,
                      }).format(Number(offering.tax_rate))}
                      %{offering.tax_label ? ` · ${offering.tax_label}` : ""})
                    </p>
                  )}
                  {manage && !service.archived && (
                    <div className="flex flex-wrap gap-3">
                      <Button
                        variant="outline"
                        render={
                          <Link
                            href={editLocationServicePath(
                              locationId,
                              service.id,
                            )}
                          />
                        }
                      >
                        {t("services.edit")}
                      </Button>
                      <Button
                        variant="outline"
                        render={
                          <Link
                            href={
                              offering
                                ? editLocationServiceOfferingPath(
                                    locationId,
                                    offering.id,
                                  )
                                : newLocationServiceOfferingPath(locationId, {
                                    service_id: service.id,
                                  })
                            }
                          />
                        }
                      >
                        {t("services.configure")}
                      </Button>
                      <Form
                        action={locationServiceArchivalPath(
                          locationId,
                          service.id,
                        )}
                        method="post"
                      >
                        {({ processing }) => (
                          <>
                            <input
                              type="hidden"
                              name="revision"
                              value={service.revision}
                            />
                            <Button
                              variant="outline"
                              type="submit"
                              disabled={processing}
                            >
                              {t("services.archive")}
                            </Button>
                          </>
                        )}
                      </Form>
                    </div>
                  )}
                </article>
              );
            })}
          </CardContent>
        </Card>
      </PageCanvas>
    </AppLayout>
  );
}
